import React, { useState, useEffect } from 'react';
import { useWeather } from '../../context';
import { useAuth } from '../../hooks/useAuth';
import { X } from 'lucide-react';
import { ThresholdTable } from './ThresholdTable';
import { PayoutResults } from './PayoutResults';
import { calculatePayouts } from '../../utils/payoutCalculator';
import { PayoutStructure } from '../../types/payout';
import { fetchPayoutStructures, savePayoutStructure, updatePayoutStructure } from '../../services/payoutService';

export function PayoutTable() {
  const { setShowPayoutTable, weatherData, modelSelect, parameterSelect } = useWeather();
  const { user } = useAuth();
  const [structures, setStructures] = useState<PayoutStructure[]>([]);
  const [selectedStructure, setSelectedStructure] = useState<PayoutStructure | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [structureName, setStructureName] = useState('');

  useEffect(() => {
    if (user) {
      loadPayoutStructures();
    }
  }, [user]);

  const loadPayoutStructures = async () => {
    try {
      const data = await fetchPayoutStructures(user!.id);
      setStructures(data);
      setSelectedStructure(data.find(s => s.is_default) || data[0] || null);
    } catch (error) {
      console.error('Error loading payout structures:', error);
    }
  };

  const handleSave = async () => {
    if (!user || !selectedStructure) return;

    try {
      if (editMode) {
        await updatePayoutStructure(selectedStructure.id, {
          ...selectedStructure,
          name: structureName
        });
      } else {
        await savePayoutStructure({
          user_id: user.id,
          name: structureName,
          calculation_basis: selectedStructure.calculation_basis,
          policy_limit: selectedStructure.policy_limit,
          thresholds: selectedStructure.thresholds,
          is_default: false
        });
      }
      await loadPayoutStructures();
      setEditMode(false);
      setStructureName('');
    } catch (error) {
      console.error('Error saving payout structure:', error);
    }
  };

  const results = selectedStructure ? calculatePayouts({
    thresholds: selectedStructure.thresholds,
    calculationBasis: selectedStructure.calculation_basis,
    policyLimit: selectedStructure.policy_limit,
    weatherData,
    modelSelect,
    parameterSelect
  }) : null;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Payout Configuration</h2>
        <button
          onClick={() => setShowPayoutTable(false)}
          className="text-[var(--secondary-text)] hover:text-[var(--primary-text)]"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      <div>
        <label className="block text-sm font-medium text-[var(--secondary-text)] mb-2">
          Payout Structure
        </label>
        <select
          value={selectedStructure?.id || ''}
          onChange={(e) => {
            const structure = structures.find(s => s.id === e.target.value);
            if (structure) {
              setSelectedStructure(structure);
              setEditMode(false);
              setStructureName('');
            }
          }}
          className="input w-full mb-2"
        >
          {structures.map(structure => (
            <option key={structure.id} value={structure.id}>
              {structure.name} {structure.is_default ? '(Default)' : ''}
            </option>
          ))}
        </select>

        {selectedStructure && (
          <>
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => {
                  setEditMode(true);
                  setStructureName(selectedStructure.name);
                }}
                className="btn btn-secondary"
              >
                Edit
              </button>
              <button
                onClick={() => {
                  setEditMode(false);
                  setStructureName('');
                  setSelectedStructure({
                    ...selectedStructure,
                    name: 'New Structure'
                  });
                }}
                className="btn"
              >
                Save As New
              </button>
            </div>

            {(editMode || structureName) && (
              <div className="mb-4">
                <input
                  type="text"
                  value={structureName}
                  onChange={(e) => setStructureName(e.target.value)}
                  placeholder="Structure name"
                  className="input w-full mb-2"
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleSave}
                    className="btn"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setEditMode(false);
                      setStructureName('');
                    }}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-[var(--secondary-text)] mb-2">
                Calculation Basis
              </label>
              <select
                value={selectedStructure.calculation_basis}
                onChange={(e) => setSelectedStructure({
                  ...selectedStructure,
                  calculation_basis: e.target.value as 'occurrence' | 'aggregate'
                })}
                className="input w-full"
                disabled={!editMode && !structureName}
              >
                <option value="occurrence">Occurrence-Based</option>
                <option value="aggregate">Aggregate-Based</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-[var(--secondary-text)] mb-2">
                Policy Limit
              </label>
              <input
                type="number"
                value={selectedStructure.policy_limit}
                onChange={(e) => setSelectedStructure({
                  ...selectedStructure,
                  policy_limit: Number(e.target.value)
                })}
                className="input w-full"
                disabled={!editMode && !structureName}
              />
            </div>

            <ThresholdTable
              thresholds={selectedStructure.thresholds}
              setThresholds={(thresholds) => setSelectedStructure({
                ...selectedStructure,
                thresholds
              })}
              disabled={!editMode && !structureName}
            />

            {results && <PayoutResults results={results} />}
          </>
        )}
      </div>
    </div>
  );
}